import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Container, Row } from 'reactstrap';

//Import Icons
import FeatherIcon from 'feather-icons-react';

// Import Images
import ImageChrome from '../../assets/images/chrome.png';
import ImageFirefox from '../../assets/images/firefox.png';
import ImageEdge from '../../assets/images/edge.png';
import ImageAppStore from '../../assets/images/app_store_badge_564x168.png';
import ImageGooglePlay from '../../assets/images/google_play_badge_trimmed_564x168.png';

import StoreBadges from '../../components/Shared/StoreBadges';
import { STORE_LINKS } from '../../utils/platform';

// Modal Video
import ModalVideo from 'react-modal-video';
import '../../../node_modules/react-modal-video/scss/modal-video.scss';

// Real entry from the HighVocab word store (same data the extension demo used),
// with three more everyday senses added.
const MILD = {
  word: 'mild',
  pronunciation: 'maɪld',
  translation: '穏やかな',
  cefr_level: 'A2',
  category: 'daily life',
  image_url: 'https://images.pexels.com/photos/6913391/pexels-photo-6913391.jpeg?auto=compress&cs=tinysrgb&w=700&h=300&fit=crop',
  audio_url: 'https://audio.convolines.co/hv_lexical_entries/3943.mp3',
  senses: [
    { part_of_speech: 'adjective', definition: 'not strong or severe; gentle or mild in degree', example: 'The doctor said it was just a mild cold.' },
    { part_of_speech: 'adjective', definition: 'not strong in flavor or spice; gentle taste', example: 'This curry has a mild, comforting flavor.' },
    { part_of_speech: 'adjective', definition: 'of weather: not very cold, or warmer than usual', example: 'We had a mild winter this year.' },
    { part_of_speech: 'adjective', definition: 'of a person: calm and gentle; not easily angered', example: 'He is a mild man who rarely raises his voice.' },
    { part_of_speech: 'adjective', definition: 'of a product: gentle and not harsh on the skin', example: 'Use a mild soap if you have sensitive skin.' },
  ],
  forms: ['milder', 'mildest', 'mildly', 'mildness'],
  similar: ['gentle', 'slight', 'soft', 'moderate', 'calm'],
  often_appears_as: ['It was just a mild headache.', 'The weather is mild for October.', 'I was mildly surprised.'],
};

const playAudio = () => {
  const audio = new Audio(MILD.audio_url);
  audio.play().catch(() => {});
};

// Bold-purple every form of the word (mild, milder, mildly…) inside an example.
const highlightWord = (text) =>
  text.split(new RegExp(`(${MILD.word}\\w*)`, 'gi')).map((part, i) =>
    i % 2 === 1 ? <strong key={i} className="word-highlight">{part}</strong> : part
  );

// Swipeable meanings, like the app's Word Detail screen. Native scroll-snap, so it
// swipes on touch and scrolls with a trackpad; the arrows and dots jump to a slide.
const SenseCarousel = ({ senses }) => {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  // Distance between slide starts: slide width plus the CSS gap.
  const slideStep = () => {
    const [first, second] = trackRef.current.children;
    return second ? second.offsetLeft - first.offsetLeft : trackRef.current.clientWidth;
  };

  const onScroll = () => {
    setActive(Math.round(trackRef.current.scrollLeft / slideStep()));
  };

  const goTo = (i) => {
    const next = Math.max(0, Math.min(senses.length - 1, i));
    setActive(next);
    trackRef.current.scrollTo({ left: next * slideStep(), behavior: 'smooth' });
  };

  return (
    <div>
      <div className="sense-track" ref={trackRef} onScroll={onScroll}>
        {senses.map((sense, i) => (
          <div className="sense-slide" key={i}>
            <div className="d-flex justify-content-between align-items-baseline">
              <p className="sense-pos">{sense.part_of_speech}</p>
              <span className="sense-counter">
                {i + 1}/{senses.length}
              </span>
            </div>
            <p className="sense-def">{sense.definition}</p>
            <p className="sense-example">{highlightWord(sense.example)}</p>
          </div>
        ))}
      </div>
      <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 10 }}>
        <button type="button" className="sense-arrow" aria-label="Previous meaning" disabled={active === 0} onClick={() => goTo(active - 1)}>
          <FeatherIcon icon="arrow-left" style={{ width: 20, height: 20 }} />
        </button>
        <div className="d-flex" style={{ gap: 6 }}>
          {senses.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Meaning ${i + 1}`}
              onClick={() => goTo(i)}
              className={`sense-dot${i === active ? ' sense-dot--active' : ''}`}
            />
          ))}
        </div>
        <button type="button" className="sense-arrow" aria-label="Next meaning" disabled={active === senses.length - 1} onClick={() => goTo(active + 1)}>
          <FeatherIcon icon="arrow-right" style={{ width: 20, height: 20 }} />
        </button>
      </div>
    </div>
  );
};

const DETAIL_TABS = ['Meanings', 'Commonly used', 'Related words', 'Word forms'];

// A readable Word Detail screen for phones, laid out like the app: header, then tabs.
const AppPreview = () => {
  const [tab, setTab] = useState(DETAIL_TABS[0]);
  const paneClass = (name) => `word-detail-pane${tab === name ? '' : ' word-detail-pane--hidden'}`;

  return (
    <div className="app-preview" aria-label="HighVocab word detail preview">
      <div className="app-preview-card word-detail">
        <div className="word-detail-head">
          <div className="d-flex align-items-start justify-content-between">
            <div>
              <span className="word-detail-word">{MILD.word}</span>
              <div className="d-flex align-items-center" style={{ gap: 6, marginTop: 2 }}>
                <span className="app-preview-ipa">/{MILD.pronunciation}/</span>
                <button type="button" onClick={playAudio} aria-label="Play pronunciation" className="word-detail-audio">
                  <FeatherIcon icon="volume-2" style={{ width: 16, height: 16 }} />
                </button>
              </div>
            </div>
            <div className="d-flex align-items-center" style={{ gap: 10, marginTop: 6 }}>
              <span className="word-detail-translation">{MILD.translation}</span>
              <FeatherIcon icon="bookmark" style={{ width: 22, height: 22, color: '#7c3aed' }} />
            </div>
          </div>
          <div className="d-flex" style={{ gap: 6, marginTop: 8 }}>
            <span className="app-preview-tag app-preview-tag--level">{MILD.cefr_level}</span>
            <span className="app-preview-tag">{MILD.category}</span>
          </div>
        </div>

        <div className="word-detail-tabs" role="tablist">
          {DETAIL_TABS.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={`word-detail-tab${tab === t ? ' word-detail-tab--active' : ''}`}
              onClick={(e) => {
                setTab(t);
                // Bring a partly hidden tab fully into view by scrolling only the tab row.
                const btn = e.currentTarget;
                const row = btn.parentElement;
                row.scrollTo({ left: btn.offsetLeft - (row.clientWidth - btn.offsetWidth) / 2, behavior: 'smooth' });
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {/* All panes share one grid cell, so the card is always as tall as the tallest
            tab and switching tabs never moves the page below it. */}
        <div className="word-detail-panel">
          <div role="tabpanel" className={paneClass('Meanings')} aria-hidden={tab !== 'Meanings'}>
            <img src={MILD.image_url} alt="Soft rose petals on white linen" className="word-detail-img" />
            <SenseCarousel senses={MILD.senses} />
          </div>
          <div role="tabpanel" className={paneClass('Commonly used')} aria-hidden={tab !== 'Commonly used'}>
            {MILD.often_appears_as.map((ex) => (
              <p key={ex} className="word-detail-phrase">{highlightWord(ex)}</p>
            ))}
          </div>
          <div role="tabpanel" className={paneClass('Related words')} aria-hidden={tab !== 'Related words'}>
            <div className="d-flex flex-wrap" style={{ gap: 8 }}>
              {MILD.similar.map((w) => (
                <span key={w} className="word-chip">{w}</span>
              ))}
            </div>
          </div>
          <div role="tabpanel" className={paneClass('Word forms')} aria-hidden={tab !== 'Word forms'}>
            <div className="d-flex flex-wrap" style={{ gap: 8 }}>
              {[MILD.word, ...MILD.forms].map((f, i) => (
                <span key={f} className={`word-chip${i === 0 ? ' word-chip--primary' : ''}`}>{f}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
      <p className="app-preview-caption">
        <strong>Every word, explained simply.</strong> Clear meanings and real examples, written for learners — across 50,000&nbsp;words.
      </p>
    </div>
  );
};

const MobileHero = ({ platform }) => (
  <section className="bg-half-170 border-bottom agency-wrapper d-table w-100 hero-mobile" id="home">
    <Container>
      <div className="title-heading text-center mt-3">
        <h1 className="heading mb-3">
          Remember the English words <span className="text-primary">you learn.</span>
        </h1>
        <p className="text-muted mb-4">
          Discover useful words, save your favorites, and practice with quizzes. Start free on your phone.
        </p>
        <StoreBadges platform={platform} height={54} />
        <p className="hero-mobile-meta mt-3 mb-0">Free to start · 50,000+ English words</p>
      </div>
      <AppPreview />
      {/* Below the card, so the arrow points past it to the extension section, not at it */}
      <p className="text-center small mt-4 mb-0">
        <span className="text-muted">On a computer?</span>{' '}
        <a href="#extension" className="fw-semibold">
          Explore the browser extension ↓
        </a>
      </p>
    </Container>
  </section>
);

const DesktopHero = () => {
  const [open, setOpen] = useState(false);
  const [videoId, setVideoId] = useState('');

  const openModal = (id) => {
    setVideoId(id);
    setOpen(true);
  };

  return (
    <section
      className="bg-half-170 border-bottom agency-wrapper d-table w-100"
      id="home"
    >
      <Container>
        <Row className="align-items-center">
          <Col lg={7} md={7} className="order-1 order-md-2">
            <div className="title-heading mt-4 ms-lg-5">
              <h1 className="heading mb-3">
                Learn English Vocabulary{' '}
                <span className="text-primary">While You Browse</span>
              </h1>
              <p className="para-desc text-muted">
                Save new words from any website and see them highlighted
                whenever they appear again.
              </p>
              {/* Stats strip */}
              <div className="d-flex flex-wrap align-items-center" style={{ gap: '8px 18px', marginTop: 4 }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>50,000+ words</span>
                <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#cbd5e1' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>60+ languages</span>
                <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#cbd5e1' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>Free to start</span>
              </div>
              {/* Primary CTA */}
              <div className="mt-4">
                <a
                  href={STORE_LINKS.chrome}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-primary btn-lg d-inline-flex align-items-center"
                  style={{ gap: 10 }}
                >
                  <img src={ImageChrome} style={{ width: 22, height: 22 }} alt="" />
                  Install on Chrome — it&apos;s free
                </a>
              </div>

              {/* Secondary install options */}
              <div className="d-flex align-items-center flex-wrap gap-3 mt-3">
                <a href={STORE_LINKS.edge} target="_blank" rel="noreferrer" className="d-inline-flex align-items-center text-muted small" style={{ gap: 6 }}>
                  <img src={ImageEdge} style={{ width: 16, height: 16 }} alt="" />
                  Edge
                </a>
                <a href={STORE_LINKS.firefox} target="_blank" rel="noreferrer" className="d-inline-flex align-items-center text-muted small" style={{ gap: 6 }}>
                  <img src={ImageFirefox} style={{ width: 16, height: 16 }} alt="" />
                  Firefox
                </a>
                <span style={{ width: 1, height: 14, background: '#e2e8f0' }} />
                <a href={STORE_LINKS.ios} target="_blank" rel="noreferrer" style={{ display: 'inline-block', lineHeight: 0, opacity: 0.85 }}>
                  <img src={ImageAppStore} alt="Download on the App Store" style={{ height: 30, width: 'auto' }} />
                </a>
                <a href={STORE_LINKS.android} target="_blank" rel="noreferrer" style={{ display: 'inline-block', lineHeight: 0, opacity: 0.85 }}>
                  <img src={ImageGooglePlay} alt="Get it on Google Play" style={{ height: 30, width: 'auto' }} />
                </a>
              </div>

              {/* Video links */}
              <div className="d-flex flex-wrap gap-3 mt-4">
                <div className="d-flex align-items-center gap-2">
                  <Link to="#" onClick={() => openModal('ccO9w6J8MeE')} className="btn btn-icon btn-pills btn-primary lightbox">
                    <FeatherIcon icon="video" className="icons" />
                  </Link>
                  <span className="fw-bold text-uppercase small">How to use</span>
                </div>
              </div>
            </div>

            <ModalVideo
              channel="youtube"
              isOpen={open}
              videoId={videoId}
              onClose={() => setOpen(false)}
            />
          </Col>
          <Col
            lg={5}
            md={5}
            className="order-2 order-md-1 mt-4 pt-2 mt-sm-0 pt-sm-0 d-none d-md-flex align-items-center justify-content-center"
          >
            <AppPreview />
          </Col>
        </Row>
      </Container>
    </section>
  );
};

const Section = ({ platform }) =>
  platform === 'desktop' ? <DesktopHero /> : <MobileHero platform={platform} />;

export default Section;
